import {AxiosResponse} from "axios";
import React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {Api} from "../../../../helpers/api";
import {routingAddresses} from "../../../../helpers/routing";
import {HttpErrorModel} from "../../../../common/models/httpError";
import Form from "../../../../common/components/shared/form/form";
import {Utils} from "../../../../helpers/utils";
import {CheckboxListModel, RouteModel, RoutesSignCode} from "../../../models/route";
import Row from "../../../../common/components/shared/row/row";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {RelationType} from "../../../../transportation/models/relation";
import {RouteService} from "../../../services/routeService";
import Checkbox from "../../../../common/components/shared/checkbox/checkbox";
import i18n from "../../../../i18n/i18n";
import {CargoType} from "../../../../transportation/models/cargo";
import {InputLabel} from "@mui/material";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {LocationDropDownEntryModel, LocationType} from "../../../models/location";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {DropDownModel} from "../../../../common/models/dropDown";
import "./routeForm.scss";
import Error from "../../../../common/components/shared/error/error";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {TransportationService} from "../../../../transportation/services/transportationService";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import Loader from "../../../../common/components/shared/loader/loader";

interface RouteFormState {
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    relationTypes: RelationType[] | [];
    cargoTypes: CargoType[] | [];
    locations: LocationDropDownEntryModel[] | [];
    signCodes: RoutesSignCode[];
    isNoAutomaticLocation: boolean;
    isRelationTypeCheckboxError: boolean;
    isCargoTypeCheckboxError: boolean;
    relationTypesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    cargoTypesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    locationsResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    signCodesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    supportedRelationTypes: CheckboxListModel[] | [];
    supportedCargoTypes: CheckboxListModel[] | [];
}

interface RouteFormProps extends RouteComponentProps {
    routeData?: RouteModel | null;
    isUpdate: boolean;
}

const routeValidators = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

class RouteForm extends React.Component <RouteFormProps, RouteFormState> {
    private name!: ValidatedValue<string>;
    private locationId!: ValidatedValue<string>;
    private sign!: ValidatedValue<string>;
    private alternativeSign!: ValidatedValue<string>;

    constructor(props: any) {
        super(props);

        this.name = new ValidatedValue("", [routeValidators]);
        this.locationId = new ValidatedValue("", [routeValidators]);
        this.sign = new ValidatedValue("");
        this.alternativeSign = new ValidatedValue("");

        this.state = {
            isSubmitButtonBlocked: false,
            response: undefined,
            relationTypes: [],
            cargoTypes: [],
            locations: [],
            signCodes: [],
            relationTypesResponse: undefined,
            cargoTypesResponse: undefined,
            locationsResponse: undefined,
            signCodesResponse: undefined,
            isNoAutomaticLocation: false,
            isRelationTypeCheckboxError: false,
            isCargoTypeCheckboxError: false,
            supportedRelationTypes: [],
            supportedCargoTypes: []
        };
    }

    async componentDidMount() {
        await this.getRelationTypes();
        await this.getCargoTypes();
        await this.getLocations();
        await this.getSignCodes();

        this.setRouteData();
    }

    render(): JSX.Element {
        return (
            <Form
                class="route-form"
                renderContent={this.renderContent()}
                renderError={this.renderErrors()}
                submit={this.submit.bind(this)}
                backAddress={routingAddresses.topology.routes.list}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
            />
        );
    }

    private renderErrors() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                    translatedFields={{
                        name: i18n.t("topology.routes.httpErrors.name")
                    }}
                    translatedFieldsForEmpty={{
                        name: i18n.t("topology.routes.httpErrors.nameEmpty"),
                        supportedRelationTypes: i18n.t("topology.routes.httpErrors.supportedRelationTypesEmpty"),
                        supportedCargoTypes: i18n.t("topology.routes.httpErrors.supportedCargoTypesEmpty"),
                        locationId: i18n.t("topology.routes.httpErrors.locationIdEmpty"),
                        sign: i18n.t("topology.routes.httpErrors.signEmpty"),
                        alternativeSign: i18n.t("topology.routes.httpErrors.alternativeSignEmpty")
                    }}
                    object={i18n.t("topology.routes.httpErrors.object")}
                />
            );
        }
    }

    private renderContent(): JSX.Element {
        const serviceFailed = !Api.isSuccess(this.state.relationTypesResponse) || !Api.isSuccess(this.state.cargoTypesResponse) ||
        !Api.isSuccess(this.state.locationsResponse) || !Api.isSuccess(this.state.signCodesResponse);


        if (serviceFailed || (this.state.relationTypes.length && this.state.cargoTypes.length &&
            this.state.locations.length && this.state.signCodes.length)) {
            return (
                <>
                    <Row class="flex-type gap-30">
                        <TextInput
                            className="element" label={i18n.t("topology.routes.headers.name")}
                            validatedValue={this.name} testId="route-name"/>
                    </Row>
                    <Row class="flex-type gap-30 checkboxes-section">
                        {this.renderRelationTypesCheckbox()}
                        {this.renderCargoTypesCheckbox()}
                    </Row>
                    {this.renderRouteContent()}
                </>
            );
        } else {
            return <Loader/>;
        }
    }

    private renderCheckboxError(): JSX.Element {
        return (
            <Error message={i18n.t("topology.routes.errors.oneOrMoreSelectedError")}/>
        );
    }

    private renderCheckboxNoDataError(error: string, type: "relationTypes" | "cargoTypes", testId: string): JSX.Element {
        return (
            <>
                <Error message={i18n.t("defaultNoDataLoadErrorMessage", {messageSuffix: error})}/>
                <ReloadButton
                    reloadFunc={async () => {
                        type === "relationTypes" && await this.getRelationTypes();
                        type === "cargoTypes" && await this.getCargoTypes();
                    }}
                    testId={`${testId}-reload`}
                />
            </>
        );
    }

    private renderRelationTypesCheckbox(): JSX.Element {
        const testId = "relation-types-checkbox";

        return (
            <div className="relation-types">
                <InputLabel className="label" data-testid={testId}>
                    {i18n.t("topology.routes.headers.supportedRelationTypes")}
                </InputLabel>
                {this.state.isRelationTypeCheckboxError && this.renderCheckboxError()}
                {!Api.isSuccess(this.state.relationTypesResponse) && this.renderCheckboxNoDataError(i18n.t("errorMessages.relationTypeErrorSuffix"), "relationTypes", testId)}
                <div className="checkboxes-box col-3">
                    {this.state.relationTypes && this.state.relationTypes.length > 0 && this.state.supportedRelationTypes && this.state.supportedRelationTypes.length > 0 &&
                        this.state.relationTypes.map((relationType, key) => {
                            return <Checkbox
                                key={`${relationType}-${key}`}
                                label={i18n.t(`relationType.${relationType}`)}
                                size="medium"
                                validatedValue={new ValidatedValue(this.state.supportedRelationTypes[key].value)}
                                testId={`${relationType}-checkbox`}
                                onChange={(value: boolean) => {
                                    const index = this.state.supportedRelationTypes.findIndex((x) => x.name === relationType);
                                    this.state.supportedRelationTypes[index].value = value;

                                    this.setState({
                                        isRelationTypeCheckboxError: !this.oneOrMoreSelected(this.state.supportedRelationTypes)
                                    });
                                }}
                            />;
                        })
                    }
                </div>
            </div>
        );
    }

    private renderCargoTypesCheckbox(): JSX.Element {
        const testId = "cargo-types-checkbox";

        return (
            <div className="cargo-types">
                <InputLabel className="label" data-testid={testId}>
                    {i18n.t("topology.routes.headers.supportedCargoTypes")}
                </InputLabel>
                {this.state.isCargoTypeCheckboxError && this.renderCheckboxError()}
                {!Api.isSuccess(this.state.cargoTypesResponse) && this.renderCheckboxNoDataError(i18n.t("errorMessages.cargoTypesErrorSuffix"), "cargoTypes", testId)}
                <div className="checkboxes-box col-2">
                    {this.state.cargoTypes && this.state.cargoTypes.length > 0 && this.state.supportedCargoTypes && this.state.supportedCargoTypes.length > 0 &&
                        this.state.cargoTypes.map((cargoType, key) => {
                            return <Checkbox
                                key={`${cargoType}-${key}`}
                                label={i18n.t(`cargoType.${cargoType}`)}
                                size="medium"
                                validatedValue={new ValidatedValue(this.state.supportedCargoTypes[key].value)}
                                testId={`${cargoType}-checkbox`}
                                onChange={(value: boolean) => {
                                    const index = this.state.supportedCargoTypes.findIndex((x) => x.name === cargoType);
                                    this.state.supportedCargoTypes[index].value = value;

                                    this.setState({
                                        isCargoTypeCheckboxError: !this.oneOrMoreSelected(this.state.supportedCargoTypes)
                                    });
                                }}
                            />;
                        })
                    }
                </div>
            </div>
        );
    }

    private renderRouteContent(): JSX.Element {
        return (
            <Row class="flex-type gap-30 routes-content">
                <Row class="flex-type header-section">
                    <InputLabel className="label" data-testid="basic-route-label">
                        {i18n.t("topology.routes.headers.basicRoute")}
                    </InputLabel>
                    <InputLabel className="label" data-testid="alternative-route-label">
                        {i18n.t("topology.routes.headers.alternativeRoute")}
                    </InputLabel>
                </Row>
                <Row class="flex-type route-section">
                    <div className="basic-route-column">
                        <DropDown
                            label={i18n.t("topology.routes.headers.location")}
                            validatedValue={this.locationId}
                            items={this.locationsToDropDownMap}
                            testId="route-location"
                            onChange={(newValue) => {
                                this.sign.removeValidators();
                                this.sign.clearValidation();
                                this.alternativeSign.removeValidators();
                                this.alternativeSign.clearValidation();

                                const location = this.state.locations.find((location) => location.id === newValue);

                                if (location?.locationType !== LocationType.NO_AUTOMATION) {
                                    this.sign.addValidators(routeValidators);
                                    this.alternativeSign.addValidators(routeValidators);
                                } else {
                                    this.sign.setValue("");
                                    this.alternativeSign.setValue("");
                                }

                                this.setState({
                                    isNoAutomaticLocation: location?.locationType === LocationType.NO_AUTOMATION
                                });
                            }}
                            error={!Api.isSuccess(this.state.locationsResponse) ? i18n.t("errorMessages.locationsErrorSuffix") : ""}
                            reloadFunc={() => this.getLocations()}
                        />
                        <DropDown
                            className={`${!Api.isSuccess(this.state.signCodesResponse) ? "error" : ""}`}
                            validatedValue={this.sign}
                            items={this.operatingSignDropDownMap}
                            label={i18n.t("topology.routes.headers.sign")}
                            testId="route-sign"
                            disabled={this.state.isNoAutomaticLocation}
                            withoutSort
                            error={!this.state.isNoAutomaticLocation && !Api.isSuccess(this.state.signCodesResponse) ? i18n.t("errorMessages.signCodesErrorSuffix") : ""}
                            reloadFunc={() => this.getSignCodes()}
                        />
                    </div>
                    <div className="alternative-route-column">
                        <DropDown
                            className={`${!Api.isSuccess(this.state.signCodesResponse) ? "error" : ""}`}
                            validatedValue={this.alternativeSign}
                            items={this.operatingSignDropDownMap}
                            label={i18n.t("topology.routes.headers.alternativeSign")}
                            testId="route-alternative-sign"
                            disabled={this.state.isNoAutomaticLocation}
                            withoutSort
                            error={!this.state.isNoAutomaticLocation && !Api.isSuccess(this.state.signCodesResponse) ? i18n.t("errorMessages.signCodesErrorSuffix") : ""}
                            reloadFunc={() => this.getSignCodes()}
                        />
                    </div>
                </Row>
            </Row>
        );
    }

    public get locationsToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.locations, "id");
    }

    public get operatingSignDropDownMap(): DropDownModel[] {
        if (Api.isSuccess(this.state.signCodesResponse)) {
            return DropDownUtils.listToDropDownMap(this.state.signCodes, "id", "name", this.manualOperatingSignTranslator, true);
        } else {
            return DropDownUtils.listToDropDownMap(this.state.signCodes, "id", "name");
        }
    }

    private manualOperatingSignTranslator(signCode: RoutesSignCode | string): string {
        switch (signCode) {
            case "GATE_1":
            case "GATE_2":
            case "GATE_3":
            case "GATE_4":
            case "GATE_5":
            case "GATE_6":
                return signCode.split("_")[1];
            default:
                return signCode;
        }
    }

    private oneOrMoreSelected(list: CheckboxListModel[]): boolean {
        let count = 0;
        list.forEach((element) => {
            if (element.value) count++;
        });

        return count > 0;
    }

    private getOnlySelectedTypes(list: CheckboxListModel[]): string[] {
        let result;
        if (list) {
            result = list.filter((element) => element.value).map((element) => element.name);
        } else result = [""];

        return result;
    }

    private getErrorMessage(): string {
        return this.props.isUpdate
            ? i18n.t("topology.routes.errors.modifyErrorSuffix")
            : i18n.t("topology.routes.errors.addErrorSuffix");
    }

    private setRouteData() {
        this.name.setValue(this.props.routeData?.name || "");
        this.locationId.setValue(this.props.routeData?.wayPoints[0].location.id || "");
        this.sign.setValue(this.props.routeData?.wayPoints[0].sign || "");
        this.alternativeSign.setValue(this.props.routeData?.wayPoints[0].alternativeSign || "");

        if (this.locationId && this.props.isUpdate) {
            const location = this.state.locations.find((location) => location.id === this.locationId.value);

            this.setState({
                isNoAutomaticLocation: location?.locationType === LocationType.NO_AUTOMATION
            });
        }
    }

    private setSupportedRelationType() {
        if (this.props.routeData && this.props.routeData.supportedRelationTypes && this.props.routeData.supportedRelationTypes.length) {
            this.setState({
                supportedRelationTypes: this.state.relationTypes.map((relationType) => {
                    return {
                        name: relationType,
                        value: !!this.props.routeData!.supportedRelationTypes.find((x) => x === relationType)
                    };
                })
            });
        } else {
            if (Api.isSuccess(this.state.relationTypesResponse) && this.state.relationTypes && this.state.relationTypes.length > 0) {
                this.setState({
                    supportedRelationTypes: this.state.relationTypes.map((relationType) => {
                        return {
                            name: relationType,
                            value: false
                        };
                    })
                });
            }
        }
    }

    private setSupportedCargoType() {
        if (this.props.routeData && this.props.routeData.supportedCargoTypes && this.props.routeData.supportedCargoTypes.length) {
            this.setState({
                supportedCargoTypes: this.state.cargoTypes.map((cargoType) => {
                    return {
                        name: cargoType,
                        value: !!this.props.routeData?.supportedCargoTypes.find((x) => x === cargoType)
                    };
                })
            });
        } else {
            if (Api.isSuccess(this.state.cargoTypesResponse) && this.state.cargoTypes && this.state.cargoTypes.length) {
                this.setState({
                    supportedCargoTypes: this.state.cargoTypes.map((cargoType) => {
                        return {
                            name: cargoType,
                            value: false
                        };
                    })
                });
            }
        }
    }

    public get isValid(): boolean {
        this.name.validate();
        this.locationId.validate();
        this.sign.validate();
        this.alternativeSign.validate();

        return this.name.isValid && this.locationId.isValid &&
            this.sign.isValid && this.alternativeSign.isValid &&
            this.oneOrMoreSelected(this.state.supportedRelationTypes) && this.oneOrMoreSelected(this.state.supportedCargoTypes);
    }

    private async submit() {
        this.setState({
            response: undefined,
            isRelationTypeCheckboxError: !this.oneOrMoreSelected(this.state.supportedRelationTypes),
            isCargoTypeCheckboxError: !this.oneOrMoreSelected(this.state.supportedCargoTypes)
        });

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyLocation();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.topology.routes.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyLocation() {
        if (this.props.isUpdate && this.props.routeData?.id) {
            this.setState({
                response: await RouteService.modifyRoute({
                    name: this.name.value.trim(),
                    supportedRelationTypes: this.getOnlySelectedTypes(this.state.supportedRelationTypes),
                    supportedCargoTypes: this.getOnlySelectedTypes(this.state.supportedCargoTypes),
                    wayPoints: [
                        {
                            locationId: this.locationId.value.trim(),
                            sign: this.sign.value.trim(),
                            alternativeSign: this.alternativeSign.value.trim()
                        }
                    ]
                }, this.props.routeData?.id)
            });
        } else {
            this.setState({
                response: await RouteService.addRoute({
                    name: this.name.value.trim(),
                    supportedRelationTypes: this.getOnlySelectedTypes(this.state.supportedRelationTypes),
                    supportedCargoTypes: this.getOnlySelectedTypes(this.state.supportedCargoTypes),
                    wayPoints: [
                        {
                            locationId: this.locationId.value.trim(),
                            sign: this.sign.value.trim(),
                            alternativeSign: this.alternativeSign.value.trim()
                        }
                    ]
                })
            });
        }
    }

    private async getLocations() {
        const locationsResponse = await TransportationService.getEntryOrExitAndNoAutomationLocationsForDropDown();

        this.setState({
            locations: (locationsResponse)?.data || [],
            locationsResponse
        });
    }

    private async getSignCodes() {
        const signCodesResponse = await RouteService.getSigns();

        this.setState({
            signCodes: (signCodesResponse?.data && signCodesResponse.data.length) ? signCodesResponse.data : [],
            signCodesResponse
        });
    }

    private async getRelationTypes() {
        const relationTypesResponse = await TransportationService.getAllRelationTypes();

        this.setState({
            relationTypes: (relationTypesResponse)?.data || [],
            relationTypesResponse
        });

        this.setSupportedRelationType();
    }

    private async getCargoTypes() {
        const cargoTypesResponse = await TransportationService.getAllCargoTypes();

        this.setState({
            cargoTypesResponse,
            cargoTypes: (cargoTypesResponse)?.data || [],
        });

        this.setSupportedCargoType();
    }
}

export default withRouter(RouteForm);
